'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

type Tone = 'default' | 'primary' | 'success' | 'warning' | 'danger';
type Size = 'sm' | 'md' | 'lg';
type Trend = 'up' | 'down' | 'flat';

export type KpiCardProps = {
  label: string;
  value: string | number;
  delta?: number | string;
  trend?: Trend;
  caption?: string;
  icon?: React.ReactNode;
  tone?: Tone;
  size?: Size;
  compact?: boolean;
  className?: string;
};

const toneMap: Record<
  Tone,
  { card: string; value: string; deltaUp: string; deltaDown: string }
> = {
  default: {
    card: 'bg-white/75 dark:bg-[#233559]/50 ring-1 ring-slate-200 dark:ring-white/10',
    value: 'text-[#233559] dark:text-zinc-50',
    deltaUp: 'text-[#4EBFBF]',
    deltaDown: 'text-[#F2785C]',
  },
  primary: {
    card: 'bg-[#7D468C]/10 dark:bg-[#7D468C]/30 ring-1 ring-[#7D468C]/20 dark:ring-[#7D468C]/50',
    value: 'text-[#7D468C] dark:text-[#E5CDED]',
    deltaUp: 'text-[#4EBFBF]',
    deltaDown: 'text-[#F2785C]',
  },
  success: {
    card: 'bg-[#4EBFBF]/12 dark:bg-[#4EBFBF]/25 ring-1 ring-[#4EBFBF]/30 dark:ring-[#4EBFBF]/45',
    value: 'text-[#218C8C] dark:text-[#BDEFEF]',
    deltaUp: 'text-[#218C8C] dark:text-[#BDEFEF]',
    deltaDown: 'text-[#F2785C]',
  },
  warning: {
    card: 'bg-[#F2785C]/12 dark:bg-[#F2785C]/25 ring-1 ring-[#F2785C]/30 dark:ring-[#F2785C]/45',
    value: 'text-[#C9583F] dark:text-[#FFD4CA]',
    deltaUp: 'text-[#4EBFBF]',
    deltaDown: 'text-[#F2785C]',
  },
  danger: {
    card: 'bg-[#F2785C]/14 dark:bg-[#F2785C]/30 ring-1 ring-[#F2785C]/35 dark:ring-[#F2785C]/50',
    value: 'text-[#C9583F] dark:text-[#FFD4CA]',
    deltaUp: 'text-[#4EBFBF]',
    deltaDown: 'text-[#C9583F] dark:text-[#FFD4CA]',
  },
};

const sizeMap: Record<
  Size,
  { pad: string; label: string; value: string; caption: string; icon: string }
> = {
  sm: {
    pad: 'p-3',
    label: 'text-xs',
    value: 'text-xl',
    caption: 'text-[11px]',
    icon: 'h-4 w-4',
  },
  md: {
    pad: 'p-4',
    label: 'text-sm',
    value: 'text-2xl',
    caption: 'text-xs',
    icon: 'h-5 w-5',
  },
  lg: {
    pad: 'p-6',
    label: 'text-sm',
    value: 'text-3xl',
    caption: 'text-sm',
    icon: 'h-6 w-6',
  },
};

export function KpiCard({
  label,
  value,
  delta,
  trend = 'flat',
  caption,
  icon,
  tone = 'primary',
  size = 'md',
  compact = false,
  className,
}: KpiCardProps) {
  const t = toneMap[tone];
  const s = sizeMap[size];

  const deltaValue =
    typeof delta === 'number' ? `${delta > 0 ? '+' : ''}${delta}%` : delta;

  const isUp = trend === 'up';
  const isDown = trend === 'down';
  const DeltaIcon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl shadow-sm',
        t.card,
        s.pad,
        !compact && 'min-h-[92px]',
        className
      )}
    >
      <span className='pointer-events-none absolute -right-6 -top-6 inline-flex h-16 w-16 rounded-full bg-black/5 dark:bg-white/5' />
      <span className='pointer-events-none absolute -right-2 -top-2 inline-flex h-8 w-8 rounded-full bg-black/5 dark:bg-white/5' />

      <div className='flex items-start justify-between gap-3'>
        <div className='space-y-1'>
          <div
            className={cn(
              'font-medium text-zinc-700 dark:text-zinc-300',
              s.label
            )}
          >
            {label}
          </div>
          <div className={cn('font-semibold tracking-tight', t.value, s.value)}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {caption ? (
            <div className={cn('text-zinc-500 dark:text-zinc-400', s.caption)}>
              {caption}
            </div>
          ) : null}
        </div>

        <div className='flex items-center gap-2'>
          {typeof deltaValue !== 'undefined' && (
            <div
              className={cn(
                'flex items-center gap-1 text-sm font-medium',
                isUp
                  ? t.deltaUp
                  : isDown
                    ? t.deltaDown
                    : 'text-zinc-500 dark:text-zinc-400'
              )}
            >
              <DeltaIcon className='h-4 w-4' aria-hidden />
              {deltaValue}
            </div>
          )}
          {icon ? (
            <div
              className={cn(
                'rounded-full bg-white/40 p-1 dark:bg-white/10',
                s.icon
              )}
            >
              {icon}
            </div>
          ) : null}
        </div>
      </div>

      <div className='bg-current/40 mt-3 h-0.5 w-16 rounded opacity-60' />
    </div>
  );
}
